import { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Mail, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";

const EASE = [0.16, 1, 0.3, 1];
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact({ t }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    mandate: "",
    message: "",
  });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          language: document.documentElement.lang || "en",
        }),
      });
      if (!res.ok) throw new Error("fail");
      toast.success(t.contact.success);
      setForm({ name: "", email: "", company: "", mandate: "", message: "" });
    } catch {
      toast.error(t.contact.error);
    } finally {
      setSending(false);
    }
  };

  const rows = [
    {
      icon: MapPin,
      label: t.contact.addressLabel,
      value: "45 Rue Boursault, 75017 Paris — France",
      href: "https://maps.google.com/?q=45+Rue+Boursault+75017+Paris",
      testid: "contact-address",
    },
    {
      icon: Phone,
      label: t.contact.phoneLabel,
      value: "+971 50 772 6069",
      href: "tel:+971507726069",
      testid: "contact-phone",
    },
    {
      icon: Mail,
      label: t.contact.emailLabel,
      value: "brightneck17@gmail.com",
      href: "mailto:brightneck17@gmail.com",
      testid: "contact-email",
    },
  ];

  const inputCls =
    "w-full rounded-sm border border-creamborder bg-cream px-4 py-3 text-sm text-forest placeholder:text-sand/70 outline-none transition-colors duration-300 focus:border-gold";

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative border-t border-creamborder py-24 sm:py-32"
    >
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-16 px-5 sm:px-8 lg:grid-cols-2">
        <div>
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, ease: EASE }}
            className="mb-5 flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.28em] text-amberburn"
          >
            <span className="inline-block h-1.5 w-1.5 rotate-45 bg-gold" />
            {t.contact.eyebrow}
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.1, ease: EASE }}
            className="font-serif text-2xl font-normal leading-tight text-forest sm:text-3xl lg:text-4xl"
          >
            {t.contact.title}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.2, ease: EASE }}
            className="mt-5 max-w-md text-sm leading-relaxed text-sand sm:text-base"
          >
            {t.contact.sub}
          </motion.p>

          <div className="mt-12 space-y-2">
            {rows.map((r, i) => (
              <motion.a
                key={r.testid}
                href={r.href}
                target={r.href.startsWith("http") ? "_blank" : undefined}
                rel="noreferrer"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, delay: 0.25 + i * 0.1, ease: EASE }}
                data-testid={r.testid}
                className="group flex items-center gap-5 border-b border-creamborder py-5 transition-colors hover:bg-creamdark/50"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-full border border-gold/40 text-gold transition-colors duration-300 group-hover:bg-forest group-hover:text-gold">
                  <r.icon className="h-4.5 w-4.5" strokeWidth={1.5} />
                </span>
                <span>
                  <span className="block font-mono text-[10px] uppercase tracking-[0.22em] text-sand">
                    {r.label}
                  </span>
                  <span className="mt-1 block text-sm font-semibold text-forest sm:text-base">
                    {r.value}
                  </span>
                </span>
                <ArrowUpRight className="ml-auto h-4 w-4 text-sand opacity-0 transition-all duration-300 group-hover:text-gold group-hover:opacity-100" />
              </motion.a>
            ))}
          </div>
        </div>

        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.9, delay: 0.2, ease: EASE }}
          data-testid="contact-form"
          className="rounded-sm border border-creamborder bg-creamdark/40 p-7 sm:p-9"
        >
          <h3 className="mb-7 font-serif text-xl font-medium text-forest sm:text-2xl">
            {t.contact.formTitle}
          </h3>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <input
              data-testid="contact-form-name"
              required
              value={form.name}
              onChange={set("name")}
              placeholder={t.contact.name}
              className={inputCls}
            />
            <input
              data-testid="contact-form-email"
              required
              type="email"
              value={form.email}
              onChange={set("email")}
              placeholder={t.contact.email}
              className={inputCls}
            />
            <input
              data-testid="contact-form-company"
              value={form.company}
              onChange={set("company")}
              placeholder={t.contact.company}
              className={inputCls}
            />
            <select
              data-testid="contact-form-mandate"
              value={form.mandate}
              onChange={set("mandate")}
              className={`${inputCls} ${form.mandate ? "" : "text-sand/70"}`}
            >
              <option value="">{t.contact.subject}</option>
              {t.contact.mandates.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </div>
          <textarea
            data-testid="contact-form-message"
            required
            rows={5}
            value={form.message}
            onChange={set("message")}
            placeholder={t.contact.message}
            className={`${inputCls} mt-4 resize-none`}
          />
          <button
            data-testid="contact-form-submit"
            type="submit"
            disabled={sending}
            className="group mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-forest px-7 py-3.5 text-sm font-semibold text-cream transition-all duration-300 hover:bg-forestlight disabled:opacity-60"
          >
            {sending ? t.contact.sending : t.contact.submit}
            <ArrowUpRight className="h-4 w-4 text-gold transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
        </motion.form>
      </div>
    </section>
  );
}
